// libs
import React, { useCallback, useEffect, useRef } from 'react';
import map from 'lodash/map';
import uniqBy from 'lodash/uniqBy';

import { useTranslation } from '@lyearn/i18n';
import { AttachOutlineLegacy } from '@lyearn/icons';
import { useUppy } from '@lyearn/shared/hooks/useUppy';
import { Button, Loader, Stack } from '@lyearn/ui';
import FileInput from '@/components/FileInput';
import MediaContainer from '@/components/LegacyMediaContainer';
import { editAction, MediaActions } from '@/components/LegacyMediaContainer/hooks/useMediaList';
import { MediaDisplay } from '@/components/LegacyMediaContainer/types';
import { EntityMediaFragment } from '@/features/messages/graphql/fragments/generated/MediaType';
import { adaptUploadMedia } from '@/features/messages/hooks/useAttachmentUpload';
import { ResourceResponseTypeEnum } from '@/types/schema';

const FILE_TYPE = ResourceResponseTypeEnum.File;

export interface MediaUploadProps {
  value: EntityMediaFragment[];
  onChange: (media: EntityMediaFragment[]) => void;
  hideButtonOnFileAvailable?: boolean;
  CustomUploadButtonComponent?: React.ComponentType<any>;
  CustomMediaContainer?: React.ComponentType<any>;
}

interface UploadButtonProps {
  onClick: (event: any) => void;
  mediaAvailable: boolean;
  isUploading: boolean;
}

const UploadButtonComponent = ({ onClick, mediaAvailable, isUploading }: UploadButtonProps) => {
  const { t } = useTranslation('Assignment');

  return (
    <Button
      appearance="secondary"
      disabled={mediaAvailable}
      shape="pill"
      startIcon={isUploading ? <Loader size={20} thickness={2} /> : <AttachOutlineLegacy />}
      variant="outlined"
      onClick={onClick}>
      {t('AddAttachments')}
    </Button>
  );
};

function MediaUpload(props: MediaUploadProps) {
  const {
    value = [],
    onChange,
    hideButtonOnFileAvailable,
    CustomUploadButtonComponent,
    CustomMediaContainer,
  } = props;
  const { uppy, files, removeFile, isUploading } = useUppy({
    referenceId: 'course-settings-media',
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const onMediaAction = useCallback(
    (actionId: any, media: any) => {
      switch (actionId) {
        case MediaActions.REMOVE: {
          const localFile = files[media.mediaId];
          if (localFile) {
            removeFile(localFile);
          } else {
            onChange(value.filter((_media) => _media.mediaId !== media.mediaId));
          }
        }
      }
    },
    [files, onChange, removeFile, value],
  );

  const onAddAttachments = useCallback(() => {
    inputRef.current?.click();
  }, []);

  useEffect(() => {
    if (isUploading) {
      return;
    }
    const localMedia = map(files, (file) => ({
      ...adaptUploadMedia(file as any),
      url: file.url,
    })).filter((file) => Boolean(file.url));
    const mergedMedia = uniqBy([...value, ...localMedia], 'mediaId');
    if (mergedMedia.length === value.length) {
      return;
    }
    onChange(mergedMedia);
  }, [files, isUploading, onChange, value]);

  const UploadButton = CustomUploadButtonComponent ?? UploadButtonComponent;
  const MediaContainerComponent = CustomMediaContainer ?? MediaContainer;

  return (
    <Stack alignItems="start" direction="col">
      <Stack alignItems="center" gapX={8}>
        {hideButtonOnFileAvailable && value.length > 0 ? null : (
          <UploadButton
            isUploading={isUploading}
            mediaAvailable={value.length > 0}
            onClick={onAddAttachments}
          />
        )}
      </Stack>
      <FileInput ref={inputRef} accept="image/*" uppy={uppy} />
      <Stack className="w-full flex-wrap">
        <MediaContainerComponent
          display={MediaDisplay.GRID}
          mediaActions={editAction}
          medias={value}
          onMediaAction={onMediaAction}
        />
      </Stack>
    </Stack>
  );
}

export default MediaUpload;
